import React, { useState, useEffect, useMemo } from 'react';
import * as api from '../services/api';
import { scoreToGrade } from '../utils/GradeUtils';
import PixelGradeBadge from './PixelGradeBadge';
import ClientDetailPage from './ClientDetailPage';

interface ClientSummary {
  clientId: string;
  clientName: string;
  avgScore: number;
  agentCount: number;
  onlineCount: number;
}

/**
 * Posture home: lands directly on the triage treeview for the worst-scoring
 * client, with a switcher for the rest. The old widget dashboard lives at
 * /overview — this page replaces it as the default answer to "open the app".
 */
const PostureTriagePage: React.FC = () => {
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [alertCount, setAlertCount] = useState<number>(0);
  const [fleetScore, setFleetScore] = useState<number>(0);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [summary, alerts] = await Promise.all([
          api.getComplianceSummary(),
          api.getAlerts().catch(() => []),
        ]);
        if (cancelled) return;
        // Worst client first — triage starts where the risk is
        const sorted: ClientSummary[] = [...(summary?.clients || [])].sort(
          (a: ClientSummary, b: ClientSummary) => a.avgScore - b.avgScore
        );
        setClients(sorted);
        setFleetScore(Math.round(summary?.avgScore || 0));
        setAlertCount(Array.isArray(alerts) ? alerts.length : 0);
        setSelectedClientId(sorted[0]?.clientId ?? null);
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load posture summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = useMemo(() => ({
    agents: clients.reduce((n, c) => n + (c.agentCount || 0), 0),
    online: clients.reduce((n, c) => n + (c.onlineCount || 0), 0),
  }), [clients]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-anchor-blue"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-anchor-card border border-red-400/20 rounded-xl p-8 text-center">
        <p className="text-red-400 text-sm">{loadError}</p>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-12 text-center">
        <p className="text-white/40 text-sm">No clients yet — enroll agents to start tracking posture.</p>
      </div>
    );
  }

  const fleetGrade = fleetScore > 0 ? scoreToGrade(fleetScore) : null;
  const worstClientId = clients[0]?.clientId;

  return (
    <div className="space-y-4">
      {/* Fleet overview strip — the old dashboard compressed to one row */}
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className="text-[10px] font-medium text-white/30 uppercase tracking-wider">Fleet</span>
        <span className="text-white/60">{clients.length} client{clients.length === 1 ? '' : 's'} · {totals.agents} agent{totals.agents === 1 ? '' : 's'} · {totals.online} online</span>
        {fleetGrade && (
          <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/[0.08]" style={{ color: fleetGrade.color, backgroundColor: `${fleetGrade.color}14` }}>
            {fleetScore}% {fleetGrade.grade}
          </span>
        )}
        {alertCount > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-400/10 text-red-400 border border-red-400/20">
            {alertCount} alert{alertCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Client switcher — worst first, auto-selected */}
      <div className="flex items-center gap-2 flex-wrap">
        {clients.map((c) => {
          const isSelected = c.clientId === selectedClientId;
          const g = scoreToGrade(c.avgScore);
          return (
            <button
              key={c.clientId}
              onClick={() => setSelectedClientId(c.clientId)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-colors text-left ${
                isSelected
                  ? 'bg-anchor-blue/10 border-anchor-blue/50'
                  : 'bg-anchor-card border-white/[0.06] opacity-60 hover:opacity-100 hover:border-white/[0.15]'
              }`}
            >
              <PixelGradeBadge score={c.avgScore} size={30} />
              <span>
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">{c.clientName}</span>
                  {c.clientId === worstClientId && clients.length > 1 && (
                    <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-400/10 text-red-400">worst</span>
                  )}
                </span>
                <span className="block text-[11px] text-white/40">
                  <span style={{ color: g.color }}>{g.grade}</span> · {c.avgScore}% · {c.onlineCount}/{c.agentCount} online
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* The triage view for the selected client */}
      {selectedClientId && <ClientDetailPage clientId={selectedClientId} embedded />}
    </div>
  );
};

export default PostureTriagePage;
